// src/domain/merchant-agent/stream.ts
var MERCHANT_AGENT_MAX_MESSAGE_LENGTH = 4e3;
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function readMerchantAgentPreview(value) {
  if (!isRecord(value) || typeof value.summary !== "string") return void 0;
  return { summary: value.summary, ..."before" in value && { before: value.before }, ..."after" in value && { after: value.after } };
}
var CHANGE_STATES = /* @__PURE__ */ new Set(["applied", "undone", "not_undoable"]);
function readMerchantAgentChange(value) {
  if (!isRecord(value) || typeof value.changeID !== "string" || typeof value.summary !== "string" || !CHANGE_STATES.has(value.state)) return void 0;
  return {
    changeID: value.changeID,
    ...typeof value.toolName === "string" && { toolName: value.toolName },
    summary: value.summary,
    ..."before" in value && { before: value.before },
    ..."after" in value && { after: value.after },
    state: value.state,
    ...typeof value.reason === "string" && { reason: value.reason },
    ...typeof value.approvalID === "string" && { approvalID: value.approvalID }
  };
}
function readMerchantAgentToolCard(toolName, toolCallID, result) {
  let value = result;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return void 0;
    }
  }
  if (!isRecord(value) || typeof value.card !== "string") return void 0;
  return { card: value.card, toolName, ...toolCallID !== void 0 && { toolCallID }, result: value };
}
function readChunk(chunk) {
  if (chunk.type === "pending_action") return { ...chunk, preview: readMerchantAgentPreview(chunk.preview) };
  if (chunk.type === "applied_change") {
    const change = readMerchantAgentChange(chunk);
    return change ? { type: "applied_change", ...change } : null;
  }
  return chunk;
}
function toEvent(data) {
  let value;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isRecord(value) || !isRecord(value.chunk) || typeof value.chunk.type !== "string" || typeof value.conversationID !== "string") return null;
  const chunk = readChunk(value.chunk);
  return chunk ? { conversationID: value.conversationID, runID: String(value.runID ?? ""), chunk } : null;
}
function parseMerchantAgentEventStream(text) {
  const blocks = text.replace(/\r\n?/g, "\n").split("\n\n");
  const rest = blocks.pop() ?? "";
  const events = blocks.flatMap((block) => {
    const data = block.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).replace(/^ /, "")).join("\n");
    const event = data ? toEvent(data) : null;
    return event ? [event] : [];
  });
  return { events, rest };
}
async function readMerchantAgentEventStream(body, onEvent) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (; ; ) {
    const { done, value } = await reader.read();
    buffer += done ? `${decoder.decode()}

` : decoder.decode(value, { stream: true });
    const { events, rest } = parseMerchantAgentEventStream(buffer);
    buffer = rest;
    events.forEach(onEvent);
    if (done) return;
  }
}

// src/domain/merchant-agent/chat-state.ts
function createMerchantAgentChatState() {
  return {
    conversationID: null,
    messages: [],
    pending: [],
    decided: [],
    changes: [],
    cards: [],
    loading: false,
    streaming: false,
    tool: null,
    deciding: null,
    undoing: null,
    notice: null,
    off: false,
    capped: false,
    resetsAt: null
  };
}
var REFUSAL_NOTICES = {
  merchant_agent_conversation_busy: "busy",
  merchant_agent_action_already_decided: "decided",
  merchant_agent_action_expired: "expired",
  merchant_agent_conversation_not_found: "notFound",
  merchant_agent_action_not_found: "notFound"
};
function merchantAgentRefusalNotice(code) {
  return REFUSAL_NOTICES[code ?? ""] ?? "failed";
}
function isMerchantAgentReloadingNotice(notice) {
  return notice === "busy" || notice === "decided" || notice === "expired";
}
function withMerchantAgentChange(changes, change, position) {
  if (!changes.some((item) => item.change.changeID === change.changeID)) return [...changes, { change, position }];
  return changes.map((item) => item.change.changeID === change.changeID ? { change, position: item.position } : item);
}
function withMerchantAgentCard(cards, card, position) {
  if (card.toolCallID !== void 0 && cards.some((item) => item.card.toolCallID === card.toolCallID)) return cards;
  return [...cards, { card, position }];
}
var DECISIONS = /* @__PURE__ */ new Map([["confirmed", "confirm"], ["declined", "decline"]]);
function merchantAgentStateFromDetail(detail) {
  return {
    messages: detail.messages.map(({ role, text }) => ({ role, text })),
    pending: detail.pendingActions.map(({ approvalID, toolName, input, preview }) => ({ approvalID, toolName, input, preview: readMerchantAgentPreview(preview) })),
    decided: (detail.decidedActions ?? []).flatMap(({ approvalID, toolName, input, preview, decision, messageIndex }) => {
      const known = DECISIONS.get(decision);
      return known ? [{ action: { approvalID, toolName, input, preview: readMerchantAgentPreview(preview) }, decision: known, position: messageIndex }] : [];
    }),
    changes: (detail.appliedChanges ?? []).reduce((changes, item) => {
      const change = readMerchantAgentChange(item);
      return change ? withMerchantAgentChange(changes, change, item.messageIndex) : changes;
    }, []),
    cards: (detail.cards ?? []).reduce((cards, item) => {
      const card = readMerchantAgentToolCard(item.toolName, item.toolCallID, item.result);
      return card ? withMerchantAgentCard(cards, card, item.messageIndex) : cards;
    }, []),
    notice: detail.status === "running" ? "running" : null
  };
}
function markMerchantAgentChangeUndone(state, change) {
  return { ...state, undoing: null, changes: withMerchantAgentChange(state.changes, change, state.messages.length) };
}
var REFUSED_STATES = {
  alreadyUndone: "undone",
  changedSince: "not_undoable",
  notUndoable: "not_undoable",
  notFound: "not_undoable"
};
function markMerchantAgentUndoRefused(state, changeID, refusal) {
  const refusedState = REFUSED_STATES[refusal];
  return {
    ...state,
    undoing: null,
    changes: state.changes.map((item) => item.change.changeID === changeID ? { ...item, change: refusedState ? { ...item.change, state: refusedState } : item.change, refusal } : item)
  };
}
function merchantAgentUndoRefusal(status, body) {
  if (body.code === "merchant_agent_change_not_found" || status === 404) return "notFound";
  if (body.code === "merchant_agent_change_already_undone") return "alreadyUndone";
  if (body.code === "merchant_agent_change_not_undoable") return body.refusal === "changed_since" ? "changedSince" : "notUndoable";
  return "failed";
}
function markMerchantAgentActionDecided(state, approvalID, decision) {
  const action = state.pending.find((item) => item.approvalID === approvalID);
  return {
    ...state,
    deciding: null,
    pending: state.pending.filter((item) => item.approvalID !== approvalID),
    decided: action ? [...state.decided, { action, decision, position: state.messages.length }] : state.decided
  };
}
function lastAssistantText(messages) {
  const last = messages.at(-1);
  return last?.role === "assistant" ? last.text : void 0;
}
function withAssistantText(messages, text) {
  const kept = lastAssistantText(messages) === void 0 ? messages : messages.slice(0, -1);
  return [...kept, { role: "assistant", text }];
}
function applyMerchantAgentChunk(state, chunk) {
  switch (chunk.type) {
    case "content":
      if (chunk.stage === "start") return { ...state, tool: null, messages: [...state.messages, { role: "assistant", text: "" }] };
      return {
        ...state,
        messages: withAssistantText(state.messages, chunk.stage === "delta" ? `${lastAssistantText(state.messages) ?? ""}${chunk.delta ?? ""}` : chunk.content ?? lastAssistantText(state.messages) ?? "")
      };
    case "tool_status": {
      const card = chunk.status === "success" ? readMerchantAgentToolCard(chunk.toolName, chunk.toolCallId, chunk.result) : void 0;
      return {
        ...state,
        tool: chunk.status === "start" ? chunk.toolName : null,
        ...card && { cards: withMerchantAgentCard(state.cards, card, state.messages.length) }
      };
    }
    case "pending_action": {
      const { approvalID, toolName, input, preview } = chunk;
      return { ...state, pending: [...state.pending.filter((action) => action.approvalID !== approvalID), { approvalID, toolName, input, preview }] };
    }
    case "applied_change": {
      const change = readMerchantAgentChange(chunk);
      return change ? { ...state, changes: withMerchantAgentChange(state.changes, change, state.messages.length) } : state;
    }
    case "error":
      return { ...state, notice: "failed" };
    case "done":
      return { ...state, tool: null, notice: chunk.reason === "error" ? "failed" : state.notice };
    default:
      return state;
  }
}

// src/domain/merchant-agent/links.ts
function resolveMerchantAgentHttpsLink(href) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  return url.protocol === "https:" ? { href: url.href, external: true } : null;
}

// src/domain/merchant-agent/markdown.ts
var INLINE = /`([^`]+)`|\*\*(.+?)\*\*|\*([^*\s][^*]*)\*|\[([^\]]+)\]\(([^)\s]+)\)/;
var FENCE = /^\s*```/;
var HEADING = /^ {0,3}#{1,6}[ \t](.*)$/;
var LIST_ITEM = /^[ \t]*([-*+]|\d+[.)])[ \t](.*)$/;
var LIST_CONTINUATION = /^[ \t]+\S/;
var TABLE_ROW = /^\s*\|.*\|\s*$/;
var TABLE_DIVIDER = /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/;
function parseMerchantAgentInline(text) {
  const inlines = [];
  let rest = text;
  while (rest) {
    const match = INLINE.exec(rest);
    if (!match) {
      inlines.push({ type: "text", text: rest });
      break;
    }
    if (match.index > 0) inlines.push({ type: "text", text: rest.slice(0, match.index) });
    const [, code, strong, em, label, href] = match;
    if (code !== void 0) inlines.push({ type: "code", text: code });
    else if (strong !== void 0) inlines.push({ type: "strong", children: parseMerchantAgentInline(strong) });
    else if (em !== void 0) inlines.push({ type: "em", children: parseMerchantAgentInline(em) });
    else inlines.push({ type: "link", href, children: parseMerchantAgentInline(label) });
    rest = rest.slice(match.index + match[0].length);
  }
  return inlines;
}
function tableCells(line) {
  return line.trim().replace(/^\||\|$/g, "").split("|").map((cell) => parseMerchantAgentInline(cell.trim()));
}
function startsTable(lines, index) {
  return TABLE_ROW.test(lines[index]) && TABLE_DIVIDER.test(lines[index + 1] ?? "");
}
function listItemKind(marker) {
  return /\d/.test(marker) ? `ordered${marker.slice(-1)}` : marker;
}
function startsBlock(lines, index) {
  const line = lines[index];
  return FENCE.test(line) || HEADING.test(line) || LIST_ITEM.test(line) || startsTable(lines, index);
}
function parseMerchantAgentMarkdown(source) {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    const heading = HEADING.exec(line);
    const listItem = LIST_ITEM.exec(line);
    if (!line.trim()) {
      index++;
    } else if (FENCE.test(line)) {
      const body = [];
      index++;
      while (index < lines.length && !FENCE.test(lines[index])) body.push(lines[index++]);
      index++;
      blocks.push({ type: "code", text: body.join("\n") });
    } else if (heading) {
      blocks.push({ type: "heading", children: parseMerchantAgentInline(heading[1].trim()) });
      index++;
    } else if (startsTable(lines, index)) {
      const header = tableCells(line);
      const rows = [];
      index += 2;
      while (index < lines.length && TABLE_ROW.test(lines[index])) rows.push(tableCells(lines[index++]));
      blocks.push({ type: "table", header, rows });
    } else if (listItem) {
      const ordered = /\d/.test(listItem[1]);
      const kind = listItemKind(listItem[1]);
      const start = ordered ? Number.parseInt(listItem[1], 10) : 1;
      const items = [];
      for (let item = listItem; item; ) {
        const children = parseMerchantAgentInline(item[2].trim());
        index++;
        while (index < lines.length && LIST_CONTINUATION.test(lines[index]) && !startsBlock(lines, index)) {
          children.push({ type: "break" }, ...parseMerchantAgentInline(lines[index++].trim()));
        }
        items.push(children);
        let next = index;
        while (next < lines.length && !lines[next].trim()) next++;
        const following = LIST_ITEM.exec(lines[next] ?? "");
        item = following && listItemKind(following[1]) === kind ? following : null;
        if (item) index = next;
      }
      blocks.push(start === 1 ? { type: "list", ordered, items } : { type: "list", ordered, start, items });
    } else {
      const children = [];
      do {
        if (children.length) children.push({ type: "break" });
        children.push(...parseMerchantAgentInline(lines[index++].trim()));
      } while (index < lines.length && lines[index].trim() && !startsBlock(lines, index));
      blocks.push({ type: "paragraph", children });
    }
  }
  return blocks;
}

// src/domain/merchant-agent/preview.ts
var ITEM_KEYS = [["action", "locale"], ["locale"], ["id"], ["trigger"]];
function isRecord2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isAbsent(value) {
  return value === void 0 || value === null;
}
function isFlatList(value) {
  return value.every((item) => typeof item !== "object" || item === null);
}
function sameValue(a, b) {
  if (isAbsent(a) || isAbsent(b)) return isAbsent(a) && isAbsent(b);
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => sameValue(item, b[index]));
  }
  if (isRecord2(a) && isRecord2(b)) {
    return [.../* @__PURE__ */ new Set([...Object.keys(a), ...Object.keys(b)])].every((key) => sameValue(a[key], b[key]));
  }
  return a === b;
}
function formatMerchantAgentPreviewValue(value) {
  if (isAbsent(value)) return void 0;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value) && isFlatList(value)) return value.map((item) => String(item)).join(", ");
  return JSON.stringify(value);
}
function row(field, before, after) {
  const formattedBefore = formatMerchantAgentPreviewValue(before);
  const formattedAfter = formatMerchantAgentPreviewValue(after);
  return {
    field,
    ...formattedBefore !== void 0 && { before: formattedBefore },
    ...formattedAfter !== void 0 && { after: formattedAfter },
    changed: !sameValue(before, after)
  };
}
function asLists(before, after) {
  const sides = [before, after].map((side) => isAbsent(side) ? [] : side);
  if (!sides.every((side) => Array.isArray(side) && side.every(isRecord2))) return void 0;
  const [beforeItems, afterItems] = sides;
  return beforeItems.length + afterItems.length > 0 ? [beforeItems, afterItems] : void 0;
}
function identity(item, keys) {
  return JSON.stringify(keys.map((key) => item[key]));
}
function itemKeys(lists) {
  return ITEM_KEYS.find((keys) => lists.every((items) => {
    if (!items.every((item) => keys.every((key) => typeof item[key] === "string" || typeof item[key] === "number"))) return false;
    return new Set(items.map((item) => identity(item, keys))).size === items.length;
  }));
}
function localeName(locale, { language, defaultLocaleLabel = "Default" }) {
  if (locale.toLowerCase() === "default") return defaultLocaleLabel;
  try {
    return new Intl.DisplayNames(language ? [language] : void 0, { type: "language" }).of(locale) ?? locale;
  } catch {
    return locale;
  }
}
function itemLabel(item, index, keys, options) {
  if (!keys) return `#${index + 1}`;
  return keys.map((key) => {
    const value = String(item[key]);
    if (key === "locale") return localeName(value, options);
    return key === "id" ? value : humanizeMerchantAgentIdentifier(value);
  }).join(" \xB7 ");
}
function partValue(value) {
  if (Array.isArray(value) && isFlatList(value)) return value.map((item) => String(item));
  return formatMerchantAgentPreviewValue(value);
}
function parts(item, fields) {
  return fields.flatMap((field) => {
    const value = partValue(item[field]);
    return value === void 0 ? [] : [{ field, value }];
  });
}
function listRows(field, before, after, options) {
  const keys = itemKeys([before, after]);
  const items = /* @__PURE__ */ new Map();
  const add = (side) => (item, index) => {
    const id = keys ? identity(item, keys) : String(index);
    const entry = items.get(id) ?? { label: itemLabel(item, index, keys, options) };
    entry[side] = item;
    items.set(id, entry);
  };
  before.forEach(add("before"));
  after.forEach(add("after"));
  const rows = [];
  const unchanged = [];
  for (const { label, before: was, after: is } of items.values()) {
    const fields = [.../* @__PURE__ */ new Set([...Object.keys(was ?? {}), ...Object.keys(is ?? {})])].filter((name) => !keys?.includes(name));
    const changed = !sameValue(was, is);
    const shown = changed && was && is ? fields.filter((name) => !sameValue(was[name], is[name])) : fields;
    const itemRow = {
      field,
      item: label,
      ...was && { beforeParts: parts(was, shown) },
      ...is && { afterParts: parts(is, shown) },
      changed
    };
    if (changed) rows.push(itemRow);
    else unchanged.push(itemRow);
  }
  if (unchanged.length > 0) rows.push({ field, changed: false, unchanged });
  return rows;
}
function fieldRows(field, before, after, options) {
  const lists = asLists(before, after);
  return lists ? listRows(field, ...lists, options) : [row(field, before, after)];
}
function merchantAgentPreviewRows(before, after, options = {}) {
  if (!isRecord2(before) && !isRecord2(after)) {
    return before === void 0 && after === void 0 ? [] : fieldRows("", before, after, options);
  }
  const fields = [.../* @__PURE__ */ new Set([...Object.keys(isRecord2(before) ? before : {}), ...Object.keys(isRecord2(after) ? after : {})])];
  return fields.flatMap((field) => fieldRows(field, isRecord2(before) ? before[field] : void 0, isRecord2(after) ? after[field] : void 0, options));
}
function merchantAgentPreviewRowLabel(row2) {
  const label = [row2.field && humanizeMerchantAgentIdentifier(row2.field), row2.item].filter(Boolean).join(" \xB7 ");
  return label || void 0;
}
function formatMerchantAgentPreviewItems(row2, language) {
  const labels = (row2.unchanged ?? []).map((item) => item.item ?? "");
  try {
    return new Intl.ListFormat(language ? [language] : void 0, { type: "conjunction", style: "narrow" }).format(labels);
  } catch {
    return labels.join(", ");
  }
}
function humanizeMerchantAgentIdentifier(identifier) {
  const words = identifier.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// src/hooks/useMerchantAgentChat.ts
import { useEffect, useRef, useState } from "react";
function useMerchantAgentChat({ transport, locale, capped = false, resetsAt = null, onConversationStarted, onSettled, onStoreChanged }) {
  const [state, setState] = useState(createMerchantAgentChatState);
  const conversationRef = useRef(null);
  const controllerRef = useRef(null);
  const update = (patch) => setState((current) => ({ ...current, ...patch }));
  useEffect(() => () => {
    controllerRef.current?.abort();
    conversationRef.current = null;
  }, []);
  function restart() {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    return controllerRef.current;
  }
  async function request(path, init) {
    const headers = new Headers(await transport.headers?.());
    if (init.body !== void 0) headers.set("Content-Type", "application/json");
    const url = `${transport.baseUrl}${path}`;
    return transport.fetch ? transport.fetch(url, { ...init, headers }) : fetch(url, { ...init, headers });
  }
  async function load(conversationID) {
    const controller = restart();
    conversationRef.current = conversationID;
    setState((current) => ({ ...current, conversationID, messages: [], pending: [], decided: [], changes: [], cards: [], loading: conversationID !== null, streaming: false, tool: null, deciding: null, undoing: null, notice: null }));
    if (!conversationID) return;
    try {
      const response = await request(`/conversations/${encodeURIComponent(conversationID)}`, { signal: controller.signal });
      if (!response.ok) return await refuse(response, false);
      const detail = await response.json();
      update({ loading: false, ...merchantAgentStateFromDetail(detail) });
    } catch {
      if (!controller.signal.aborted) update({ loading: false, notice: "failed" });
    }
  }
  async function refuse(response, reload) {
    const refusal = await response.json().catch(() => ({}));
    if (response.status === 403) return update({ off: true, loading: false });
    if (response.status === 429) return update({ capped: true, resetsAt: refusal.resetsAt ?? null });
    const notice = merchantAgentRefusalNotice(refusal.code);
    if (reload && isMerchantAgentReloadingNotice(notice) && conversationRef.current) await load(conversationRef.current);
    update({ notice, loading: false });
  }
  async function stream(path, body, onAccepted) {
    const controller = restart();
    update({ streaming: true, tool: null, notice: null });
    let accepted = false;
    try {
      const response = await request(path, { method: "POST", body: JSON.stringify(body), signal: controller.signal });
      if (!response.ok || !response.body) {
        await refuse(response, true);
        return false;
      }
      accepted = true;
      onAccepted?.();
      await readMerchantAgentEventStream(response.body, ({ conversationID, chunk }) => {
        if (conversationID && conversationRef.current !== conversationID) {
          conversationRef.current = conversationID;
          onConversationStarted?.(conversationID);
        }
        setState((current) => applyMerchantAgentChunk({ ...current, conversationID: conversationRef.current }, chunk));
        if (chunk.type === "applied_change") onStoreChanged?.();
      });
    } catch {
      if (!controller.signal.aborted) update({ notice: "failed" });
    } finally {
      if (!controller.signal.aborted) update({ streaming: false, tool: null, deciding: null });
      onSettled?.();
    }
    return accepted;
  }
  async function send(message) {
    const sent = { role: "user", text: message };
    setState((current) => ({ ...current, messages: [...current.messages, sent] }));
    const conversationID = conversationRef.current;
    const accepted = await stream("/messages", { message, locale, ...conversationID && { conversationID } });
    if (!accepted) setState((current) => ({ ...current, messages: current.messages.filter((item) => item !== sent) }));
    return accepted;
  }
  async function decide(approvalID, decision) {
    update({ deciding: { approvalID, decision } });
    await stream(`/actions/${encodeURIComponent(approvalID)}/${decision}`, {}, () => {
      setState((current) => markMerchantAgentActionDecided(current, approvalID, decision));
    });
  }
  async function undo(changeID) {
    const conversationID = conversationRef.current;
    update({ undoing: changeID });
    let next;
    try {
      const response = await request(`/changes/${encodeURIComponent(changeID)}/undo`, { method: "POST" });
      const body = await response.json().catch(() => null);
      const change = response.ok ? readMerchantAgentChange(body) : void 0;
      if (response.status === 403) next = (current) => ({ ...current, undoing: null, off: true });
      else if (change) next = (current) => markMerchantAgentChangeUndone(current, change);
      else next = (current) => markMerchantAgentUndoRefused(current, changeID, response.ok ? "failed" : merchantAgentUndoRefusal(response.status, body ?? {}));
      if (change) onStoreChanged?.();
    } catch {
      next = (current) => markMerchantAgentUndoRefused(current, changeID, "failed");
    }
    if (conversationRef.current === conversationID) setState(next);
  }
  function open(conversationID) {
    if (conversationID !== conversationRef.current) void load(conversationID);
  }
  return {
    state: { ...state, capped: state.capped || capped, resetsAt: state.resetsAt ?? resetsAt },
    open,
    reload: () => load(conversationRef.current),
    send,
    decide,
    undo
  };
}

export {
  MERCHANT_AGENT_MAX_MESSAGE_LENGTH,
  readMerchantAgentPreview,
  readMerchantAgentChange,
  readMerchantAgentToolCard,
  parseMerchantAgentEventStream,
  readMerchantAgentEventStream,
  createMerchantAgentChatState,
  merchantAgentRefusalNotice,
  isMerchantAgentReloadingNotice,
  merchantAgentStateFromDetail,
  markMerchantAgentChangeUndone,
  markMerchantAgentUndoRefused,
  merchantAgentUndoRefusal,
  markMerchantAgentActionDecided,
  applyMerchantAgentChunk,
  resolveMerchantAgentHttpsLink,
  parseMerchantAgentInline,
  parseMerchantAgentMarkdown,
  formatMerchantAgentPreviewValue,
  merchantAgentPreviewRows,
  merchantAgentPreviewRowLabel,
  formatMerchantAgentPreviewItems,
  humanizeMerchantAgentIdentifier,
  useMerchantAgentChat
};
//# sourceMappingURL=chunk-SRZHE2FJ.mjs.map